import {
  getNewsAudioList,
  getNewsSummary,
} from "@shared/api/voiceBot/endpoints";
import { useIsMobile } from "@shared/lib";
import { useMutation } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";

interface NewsManagerProps {
  onNewsPlayback: () => void;
}

export const useNewsManager = ({ onNewsPlayback }: NewsManagerProps) => {
  const isMobile = useIsMobile();

  const [isPlayingNews, setIsPlayingNews] = useState<boolean>(false);
  const [isNewsVisible, setIsNewsVisible] = useState<boolean>(false);
  const [isNewsPaused, setIsNewsPaused] = useState<boolean>(false);
  const [currentNewsCategory, setCurrentNewsCategory] = useState<
    string | undefined
  >(undefined);
  const [isPlayingSummary, setIsPlayingSummary] = useState<boolean>(false);
  const [hasNextPage, setHasNextPage] = useState<boolean>(false);
  const [currentPageAudio, setCurrentPageAudio] = useState<any[]>([]);
  const [currentPageIndex, setCurrentPageIndex] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [summaryContent, setSummaryContent] = useState<string>("");
  const [summaryTitle, setSummaryTitle] = useState<string>("");

  const newsAudioRef = useRef<HTMLAudioElement | null>(null);
  const hasNextPageRef = useRef<boolean>(false);
  const currentPageAudioRef = useRef<any[]>([]);
  const currentPageIndexRef = useRef<number>(0);
  const isMutedRef = useRef<boolean>(isMobile);
  const playNextNewsItemInPageRef = useRef<(() => void) | null>(null);
  const isUsingNextItemModeRef = useRef<boolean>(false);

  const [audioDuration, setAudioDuration] = useState<number>(0);
  const [isMuted, setIsMuted] = useState<boolean>(isMobile); // Muted on mobile for autoplay, unmuted on desktop

  // Keep ref in sync with state
  useEffect(() => {
    isMutedRef.current = isMuted;
  }, [isMuted]);

  const pageSize = 10;

  // ---------- NEWS AUDIO PLAYBACK ----------
  const playNextNewsAudio = useCallback(() => {
    if (currentPageIndexRef.current >= currentPageAudioRef.current.length) {
      if (hasNextPageRef.current) {
        fetchNextPage();
        return;
      } else {
        setIsPlayingNews(false);
        setIsNewsPaused(false);
        return;
      }
    }

    const nextAudio = currentPageAudioRef.current[currentPageIndexRef.current];

    currentPageIndexRef.current += 1;
    setCurrentPageIndex(currentPageIndexRef.current);

    if (newsAudioRef.current) {
      newsAudioRef.current.pause();
      newsAudioRef.current.remove();
    }

    const audio = new Audio(nextAudio.audio_url);
    audio.muted = isMutedRef.current; // Use ref to get latest mute state
    newsAudioRef.current = audio;

    // Track audio progress
    audio.addEventListener("loadedmetadata", () => {
      setAudioDuration(audio.duration);
    });

    audio.addEventListener("ended", () => {
      setTimeout(() => {
        playNextNewsAudio();
      }, 500);
    });

    audio.addEventListener("error", (e) => {
      setIsPlayingNews(false);
      setIsNewsPaused(true);

      // setTimeout(() => {
      //   playNextNewsAudio();
      // }, 1000);
    });

    audio.play().catch((error) => {
      setIsPlayingNews(false);
      setIsNewsPaused(true);

      // setTimeout(() => {
      //   playNextNewsAudio();
      // }, 1000);
    });
  }, []);

  const fetchNextPage = useCallback(() => {
    const nextPageParams = {
      page: currentPage + 1,
      page_size: pageSize,
      ...(currentNewsCategory && { category: currentNewsCategory }),
    };

    handleNewsAudioList.mutate(nextPageParams);
  }, [currentPage, currentNewsCategory, pageSize]);

  const startNewsPlayback = useCallback(
    (newsData: any[], category?: string) => {
      setCurrentNewsCategory(category);
      setIsPlayingNews(true);

      currentPageAudioRef.current = newsData;
      currentPageIndexRef.current = 0;

      setCurrentPageAudio(newsData);
      setCurrentPageIndex(0);

      playNextNewsAudio();
    },
    [playNextNewsAudio]
  );

  const stopNewsPlayback = useCallback(() => {
    if (newsAudioRef.current) {
      newsAudioRef.current.pause();
      newsAudioRef.current.remove();
    }
    setIsPlayingNews(false);
    setIsNewsPaused(false);
    setIsPlayingSummary(false);
    setCurrentPage(1);
    setHasNextPage(false);
    setCurrentPageAudio([]);
    setCurrentPageIndex(0);
    setSummaryContent("");
    setSummaryTitle("");

    hasNextPageRef.current = false;
    currentPageAudioRef.current = [];
    currentPageIndexRef.current = 0;
  }, []);

  // ---------- NEWS SUMMARY PLAYBACK ----------
  const playNewsSummary = useCallback((audioUrl: string) => {
    if (newsAudioRef.current) {
      newsAudioRef.current.pause();
      newsAudioRef.current.remove();
    }

    const audio = new Audio(audioUrl);
    audio.muted = isMutedRef.current; // Use ref to get latest mute state
    newsAudioRef.current = audio;

    // Track audio progress
    audio.addEventListener("loadedmetadata", () => {
      setAudioDuration(audio.duration);
    });

    audio.addEventListener("timeupdate", () => {
    });

    audio.addEventListener("ended", () => {
      setIsPlayingSummary(false);
      setAudioDuration(0);

      // Play news_summary_done.mp3 after summary finishes
      const doneAudio = new Audio("/audio/news_summary_done.mp3");
      doneAudio.muted = isMutedRef.current;

      doneAudio.addEventListener("ended", () => {
        setTimeout(() => {
          setIsNewsVisible(false);
          setSummaryContent("");
          setSummaryTitle("");
          onNewsPlayback();
        }, 1000); // Delay a bit to let TTS message be sent
      });

      doneAudio.addEventListener("error", (e) => {
        // Still do cleanup even if done audio fails
        setTimeout(() => {
          setIsNewsVisible(false);
          setSummaryContent("");
          setSummaryTitle("");
          onNewsPlayback();
        }, 1000);
      });

      doneAudio.play().catch((error) => {
        // Still do cleanup even if done audio fails
        setTimeout(() => {
          setIsNewsVisible(false);
          setSummaryContent("");
          setSummaryTitle("");
          onNewsPlayback();
        }, 1000);
      });
    });

    audio.addEventListener("error", (e) => {
      setIsPlayingSummary(false);
      setAudioDuration(0);
    });

    audio.play().catch((error) => {
      setIsPlayingSummary(false);
      setAudioDuration(0);
    });
  }, []);

  // ---------- NEWS CONTROL HANDLERS ----------
  const playPrevNewsAudio = useCallback(() => {
    // Can only go back if we're not at the first news item
    if (currentPageIndexRef.current <= 1) {
      return;
    }

    // Go back 2 positions: current index was already incremented when playing current news
    currentPageIndexRef.current -= 2;
    setCurrentPageIndex(currentPageIndexRef.current);

    const prevAudio = currentPageAudioRef.current[currentPageIndexRef.current];

    if (newsAudioRef.current) {
      newsAudioRef.current.pause();
      newsAudioRef.current.remove();
    }

    const audio = new Audio(prevAudio.audio_url);
    audio.muted = isMutedRef.current;
    newsAudioRef.current = audio;

    // Track audio progress
    audio.addEventListener("loadedmetadata", () => {
      setAudioDuration(audio.duration);
    });

    audio.addEventListener("timeupdate", () => {
    });

    audio.addEventListener("ended", () => {
      setTimeout(() => {
        playNextNewsAudio();
      }, 500);
    });

    audio.addEventListener("error", (e) => {
      setIsPlayingNews(false);
      setIsNewsPaused(true);
    });

    audio.play().catch((error) => {
      setIsPlayingNews(false);
      setIsNewsPaused(true);
    });

    // Increment index after starting playback (to match playNextNewsAudio behavior)
    currentPageIndexRef.current += 1;
    setCurrentPageIndex(currentPageIndexRef.current);
  }, [playNextNewsAudio]);

  const handleNextNews = useCallback(
    (requestedCategory?: string) => {
      if (
        requestedCategory &&
        requestedCategory !== currentNewsCategory &&
        isPlayingNews
      ) {
        setCurrentPage(1);
        setCurrentNewsCategory(requestedCategory);
        setHasNextPage(false);
        setCurrentPageAudio([]);
        setCurrentPageIndex(0);

        hasNextPageRef.current = false;
        currentPageAudioRef.current = [];
        currentPageIndexRef.current = 0;

        const params = {
          page: 1,
          page_size: pageSize,
          category: requestedCategory,
        };

        handleNewsAudioList.mutate(params);
        return;
      }

      playNextNewsAudio();
    },
    [isPlayingNews, playNextNewsAudio, currentNewsCategory, pageSize]
  );

  const handlePrevNews = useCallback(() => {
    if (!isPlayingNews || isPlayingSummary) {
      return;
    }
    playPrevNewsAudio();
  }, [isPlayingNews, isPlayingSummary, playPrevNewsAudio]);

  // Play next news item within the same page, auto-fetch next page if needed
  const playNextNewsItemInPage = useCallback(() => {
    // Check if we can go to next item in current page
    if (currentPageIndexRef.current >= currentPageAudioRef.current.length) {
      // If no more items in current page, try to fetch next page
      if (hasNextPageRef.current) {
        fetchNextPage();
        return;
      } else {
        setIsPlayingNews(false);
        setIsNewsPaused(false);
        return;
      }
    }

    const nextAudio = currentPageAudioRef.current[currentPageIndexRef.current];

    currentPageIndexRef.current += 1;
    setCurrentPageIndex(currentPageIndexRef.current);

    if (newsAudioRef.current) {
      newsAudioRef.current.pause();
      newsAudioRef.current.remove();
    }

    const audio = new Audio(nextAudio.audio_url);
    audio.muted = isMutedRef.current;
    newsAudioRef.current = audio;

    // Track audio progress
    audio.addEventListener("loadedmetadata", () => {
      setAudioDuration(audio.duration);
    });

    audio.addEventListener("timeupdate", () => {
    });

    audio.addEventListener("ended", () => {
      // Auto-play next item in same page if available, or fetch next page
      setTimeout(() => {
        playNextNewsItemInPageRef.current?.();
      }, 500);
    });

    audio.addEventListener("error", (e) => {
      setIsPlayingNews(false);
      setIsNewsPaused(true);
    });

    audio.play().catch((error) => {
      setIsPlayingNews(false);
      setIsNewsPaused(true);
    });
  }, [fetchNextPage]);

  // Keep ref in sync
  useEffect(() => {
    playNextNewsItemInPageRef.current = playNextNewsItemInPage;
  }, [playNextNewsItemInPage]);

  // Simple handlers for next/prev news items within the same page
  const handleNextNewsItem = useCallback(() => {
    if (!isPlayingNews || isPlayingSummary) {
      return;
    }
    isUsingNextItemModeRef.current = true;
    playNextNewsItemInPage();
  }, [isPlayingNews, isPlayingSummary, playNextNewsItemInPage]);

  const handlePrevNewsItem = useCallback(() => {
    if (!isPlayingNews || isPlayingSummary) {
      return;
    }
    playPrevNewsAudio();
  }, [isPlayingNews, isPlayingSummary, playPrevNewsAudio]);

  const handlePauseNews = useCallback(() => {
    if (newsAudioRef.current && !newsAudioRef.current.paused) {
      newsAudioRef.current.pause();
      setIsNewsPaused(true);
    }
  }, []);

  const handleResumeNews = useCallback(() => {
    if (newsAudioRef.current && newsAudioRef.current.paused) {
      newsAudioRef.current.play();
      setIsNewsPaused(false);
    }
  }, []);

  const handleToggleMute = useCallback(() => {
    if (!isMuted) return;

    const newMutedState = !isMuted;
    setIsMuted(newMutedState);
    isMutedRef.current = newMutedState; // Update ref immediately
    if (newsAudioRef.current) {
      newsAudioRef.current.muted = newMutedState;
    }
  }, [isMuted]);

  const handleStopNews = useCallback(() => {
    stopNewsPlayback();
  }, [stopNewsPlayback]);

  const stopAllAudio = useCallback(() => {
    handlePauseNews();
    if (isPlayingSummary) {
      setIsPlayingSummary(false);
      if (newsAudioRef.current) {
        newsAudioRef.current.pause();
        newsAudioRef.current.remove();
      }
    }
  }, [isPlayingSummary, handlePauseNews]);

  // ---------- NEWS API HANDLERS ----------
  const playNoNewsAudio = useCallback(() => {
    if (newsAudioRef.current) {
      newsAudioRef.current.pause();
      newsAudioRef.current.remove();
    }

    setIsNewsVisible(false);
    setSummaryContent("");
    setSummaryTitle("");
    onNewsPlayback();

    const audio = new Audio("/audio/no_news.mp3");
    audio.muted = isMutedRef.current;

    audio.addEventListener("error", (e) => {});

    audio.play().catch((error) => {});
  }, []);

  const handleNewsAudioList = useMutation({
    mutationFn: getNewsAudioList,
    onSuccess: (data: any) => {
      if (data?.items && Array.isArray(data.items) && data.items.length > 0) {
        // setIsNewsVisible(true);
        setHasNextPage(data.has_next);
        hasNextPageRef.current = data.has_next;

        currentPageAudioRef.current = data.items;
        currentPageIndexRef.current = 0;

        setCurrentPageAudio(data.items);
        setCurrentPageIndex(0);

        if (currentPage === 1) {
          startNewsPlayback(data.items, currentNewsCategory);
        } else {
          playNextNewsAudio();
        }

        setCurrentPage((prev) => prev + 1);
      } else {
        setIsPlayingNews(false);
        setIsNewsPaused(false);
        playNoNewsAudio();
      }
    },
    onError: (error: any) => {},
  });

  const handleNewsSummary = useMutation({
    mutationFn: getNewsSummary,
    onSuccess: (data: any) => {
      if (data?.url) {
        // setIsNewsVisible(true);
        // Set summary content and title if available, otherwise use placeholder
        if (data?.content) {
          setSummaryContent(data.content);
        }
        setSummaryTitle("Tóm tắt tin tức");
        playNewsSummary(data.url);
      } else {
        setIsPlayingSummary(false);
        setSummaryContent("");
        setSummaryTitle("");
        playNoNewsAudio();
      }
    },
    onError: (error: any) => {
      setIsPlayingSummary(false);
      setSummaryContent("");
      setSummaryTitle("");
    },
  });

  // ---------- NEWS INTENT HANDLER ----------
  const handleNewsIntent = useCallback(
    (data: any) => {
      if (data?.is_summary) {
        handleStopNews();
        setIsPlayingSummary(true);

        const params: { category?: string } = {};
        if (data?.category) {
          params.category = data.category;
        }

        handleNewsSummary.mutate(params);
      } else if (data?.is_next) {
        handleNextNews(data?.category);
      } else if (data?.is_pause) {
        handlePauseNews();
      } else if (data?.is_resume) {
        handleResumeNews();
      } else if (data?.is_stop) {
        handleStopNews();
      } else {
        setCurrentPage(1);
        setIsPlayingNews(false);
        setIsNewsPaused(false);
        setIsPlayingSummary(false);
        setCurrentNewsCategory(data?.category);
        setHasNextPage(false);
        setCurrentPageAudio([]);
        setCurrentPageIndex(0);

        hasNextPageRef.current = false;
        currentPageAudioRef.current = [];
        currentPageIndexRef.current = 0;

        const params: { page: number; page_size: number; category?: string } = {
          page: 1,
          page_size: pageSize,
        };

        if (data?.category) {
          params.category = data.category;
        }

        handleNewsAudioList.mutate(params);
      }
    },
    [
      pageSize,
      handleNextNews,
      handlePauseNews,
      handleResumeNews,
      handleStopNews,
      handleNewsSummary,
      handleNewsAudioList,
    ]
  );

  const getCurrentAudioTime = useCallback(() => {
    return newsAudioRef.current?.currentTime ?? 0;
  }, []);

  return {
    // State
    isNewsVisible,
    isPlayingNews,
    isNewsPaused,
    currentNewsCategory,
    isPlayingSummary,
    hasNextPage,
    currentPageAudio,
    currentPageIndex,
    currentPage,
    audioDuration,
    summaryContent,
    summaryTitle,
    isMuted,

    // Actions
    setIsNewsVisible,
    handleNewsIntent,
    handleNextNews,
    handlePrevNews,
    handleNextNewsItem,
    handlePrevNewsItem,
    handlePauseNews,
    handleResumeNews,
    handleStopNews,
    stopAllAudio,
    startNewsPlayback,
    stopNewsPlayback,
    playNewsSummary,
    handleToggleMute,
    getCurrentAudioTime,

    // Mutations
    handleNewsAudioList,
    handleNewsSummary,
  };
};
